/**
 * Team Colours
 *
 * Static map of official brand colours for every NBA (30) and NFL (32) team,
 * keyed by team abbreviation. No API — team colours don't change, so this is
 * just data.
 *
 * NAMESPACED BY SPORT — NOT A FLAT MAP. Seven codes exist in both leagues
 * with completely different colours (ATL, CHI, DAL, DEN, DET, MIN, WAS —
 * e.g. MIN is the Timberwolves' navy/blue in the NBA but the Vikings'
 * purple/gold in the NFL). A single flat object would silently pick
 * whichever sport's block was defined last for those seven, so teamColor()
 * always takes a sport. Every caller already has `league.sport` in scope
 * (NBA and NFL leagues are never mixed on one page), so this costs nothing
 * at the call site.
 *
 * ABBREVIATION KEYING — VERIFIED LIVE, NOT ASSUMED. ESPN and Sleeper do NOT
 * use the same abbreviation for every team. Probed both live before writing
 * this (site.api.espn.com/.../teams and api.sleeper.app/v1/players/{nba,nfl})
 * rather than assuming a "standard" code, per the project's
 * probe-before-you-parse rule:
 *   NBA — ESPN says GS/NO/NY/SA/UTAH/WSH, Sleeper says GSW/NOP/NYK/SAS/UTA/WAS.
 *   NFL — ESPN says WSH, Sleeper says WAS (everything else matches).
 * Each sport's map is keyed by the more widely-recognized code (GSW, NOP,
 * NYK, SAS, UTA, WAS for NBA; WAS for NFL). ABBR_ALIASES maps every
 * ESPN-only spelling (plus a couple of known-stale ones, e.g. Sleeper's
 * legacy 'OAK' for the Raiders) onto that canonical key. Yahoo wasn't probed
 * for this pass — treat any Yahoo-only mismatch as unverified until checked
 * the same way.
 *
 * `ink` is precomputed per team (contrast-checked against white, not
 * derived at runtime) — most team primaries are dark enough for white text,
 * but a couple (Lakers gold, Saints old gold) are not.
 */

const ABBR_ALIASES = {
  nba: {
    GS: 'GSW', NO: 'NOP', NY: 'NYK', SA: 'SAS', UTAH: 'UTA', WSH: 'WAS',
  },
  nfl: {
    WSH: 'WAS',       // ESPN's spelling
    OAK: 'LV',         // Sleeper's legacy Raiders code
    JAC: 'JAX', SD: 'LAC', STL: 'LAR', // older feeds
  },
}

const NBA_COLORS = {
  ATL: { primary: '#E03A3E', secondary: '#C1D32F', ink: '#FFFFFF' },
  BOS: { primary: '#007A33', secondary: '#BA9653', ink: '#FFFFFF' },
  BKN: { primary: '#000000', secondary: '#FFFFFF', ink: '#FFFFFF' },
  CHA: { primary: '#1D1160', secondary: '#00788C', ink: '#FFFFFF' },
  CHI: { primary: '#CE1141', secondary: '#000000', ink: '#FFFFFF' },
  CLE: { primary: '#860038', secondary: '#FDBB30', ink: '#FFFFFF' },
  DAL: { primary: '#00538C', secondary: '#002B5E', ink: '#FFFFFF' },
  DEN: { primary: '#0E2240', secondary: '#FEC524', ink: '#FFFFFF' },
  DET: { primary: '#C8102E', secondary: '#1D42BA', ink: '#FFFFFF' },
  GSW: { primary: '#1D428A', secondary: '#FFC72C', ink: '#FFFFFF' },
  HOU: { primary: '#CE1141', secondary: '#000000', ink: '#FFFFFF' },
  IND: { primary: '#002D62', secondary: '#FDBB30', ink: '#FFFFFF' },
  LAC: { primary: '#C8102E', secondary: '#1D428A', ink: '#FFFFFF' },
  LAL: { primary: '#FDB927', secondary: '#552583', ink: '#0A1F44' },
  MEM: { primary: '#5D76A9', secondary: '#12173F', ink: '#FFFFFF' },
  MIA: { primary: '#98002E', secondary: '#F9A01B', ink: '#FFFFFF' },
  MIL: { primary: '#00471B', secondary: '#EEE1C6', ink: '#FFFFFF' },
  MIN: { primary: '#0C2340', secondary: '#236192', ink: '#FFFFFF' },
  NOP: { primary: '#0C2340', secondary: '#C8102E', ink: '#FFFFFF' },
  NYK: { primary: '#006BB6', secondary: '#F58426', ink: '#FFFFFF' },
  OKC: { primary: '#007AC1', secondary: '#EF3B24', ink: '#FFFFFF' },
  ORL: { primary: '#0077C0', secondary: '#C4CED4', ink: '#FFFFFF' },
  PHI: { primary: '#006BB6', secondary: '#ED174C', ink: '#FFFFFF' },
  PHX: { primary: '#1D1160', secondary: '#E56020', ink: '#FFFFFF' },
  POR: { primary: '#E03A3E', secondary: '#000000', ink: '#FFFFFF' },
  SAC: { primary: '#5A2D81', secondary: '#63727A', ink: '#FFFFFF' },
  SAS: { primary: '#000000', secondary: '#C4CED4', ink: '#FFFFFF' },
  TOR: { primary: '#CE1141', secondary: '#000000', ink: '#FFFFFF' },
  UTA: { primary: '#002B5C', secondary: '#F9A01B', ink: '#FFFFFF' },
  WAS: { primary: '#002B5C', secondary: '#E31837', ink: '#FFFFFF' },
}

const NFL_COLORS = {
  ARI: { primary: '#97233F', secondary: '#000000', ink: '#FFFFFF' },
  ATL: { primary: '#A71930', secondary: '#000000', ink: '#FFFFFF' },
  BAL: { primary: '#241773', secondary: '#000000', ink: '#FFFFFF' },
  BUF: { primary: '#00338D', secondary: '#C60C30', ink: '#FFFFFF' },
  CAR: { primary: '#0085CA', secondary: '#101820', ink: '#FFFFFF' },
  CHI: { primary: '#0B162A', secondary: '#C83803', ink: '#FFFFFF' },
  CIN: { primary: '#FB4F14', secondary: '#000000', ink: '#FFFFFF' },
  CLE: { primary: '#311D00', secondary: '#FF3C00', ink: '#FFFFFF' },
  DAL: { primary: '#041E42', secondary: '#869397', ink: '#FFFFFF' },
  DEN: { primary: '#FB4F14', secondary: '#002244', ink: '#FFFFFF' },
  DET: { primary: '#0076B6', secondary: '#B0B7BC', ink: '#FFFFFF' },
  GB:  { primary: '#203731', secondary: '#FFB612', ink: '#FFFFFF' },
  HOU: { primary: '#03202F', secondary: '#A71930', ink: '#FFFFFF' },
  IND: { primary: '#002C5F', secondary: '#A2AAAD', ink: '#FFFFFF' },
  JAX: { primary: '#101820', secondary: '#D7A22A', ink: '#FFFFFF' },
  KC:  { primary: '#E31837', secondary: '#FFB81C', ink: '#FFFFFF' },
  LAC: { primary: '#0080C6', secondary: '#FFC20E', ink: '#FFFFFF' },
  LAR: { primary: '#003594', secondary: '#FFA300', ink: '#FFFFFF' },
  LV:  { primary: '#000000', secondary: '#A5ACAF', ink: '#FFFFFF' },
  MIA: { primary: '#008E97', secondary: '#FC4C02', ink: '#FFFFFF' },
  MIN: { primary: '#4F2683', secondary: '#FFC62F', ink: '#FFFFFF' },
  NE:  { primary: '#002244', secondary: '#C60C30', ink: '#FFFFFF' },
  NO:  { primary: '#D3BC8D', secondary: '#101820', ink: '#0A1F44' },
  NYG: { primary: '#0B2265', secondary: '#A71930', ink: '#FFFFFF' },
  NYJ: { primary: '#125740', secondary: '#000000', ink: '#FFFFFF' },
  PHI: { primary: '#004C54', secondary: '#A5ACAF', ink: '#FFFFFF' },
  PIT: { primary: '#101820', secondary: '#FFB612', ink: '#FFFFFF' },
  SEA: { primary: '#002244', secondary: '#69BE28', ink: '#FFFFFF' },
  SF:  { primary: '#AA0000', secondary: '#B3995D', ink: '#FFFFFF' },
  TB:  { primary: '#D50A0A', secondary: '#34302B', ink: '#FFFFFF' },
  TEN: { primary: '#0C2340', secondary: '#4B92DB', ink: '#FFFFFF' },
  WAS: { primary: '#5A1414', secondary: '#FFB612', ink: '#FFFFFF' },
}

export const TEAM_COLORS = { nba: NBA_COLORS, nfl: NFL_COLORS }

// Safe fallback for an unrecognized abbreviation/sport — never throw, never
// render an empty chip. Matches V2.faint / navy ink so it still reads as
// "a team chip", just an unbranded one.
const FALLBACK = { primary: 'rgba(10,31,68,0.40)', secondary: 'rgba(10,31,68,0.20)', ink: '#0A1F44' }

export function teamColor(abbr, sport = 'nba') {
  const key = String(abbr || '').toUpperCase().trim()
  if (!key) return FALLBACK
  const sportKey = String(sport || 'nba').toLowerCase() === 'nfl' ? 'nfl' : 'nba'
  const resolved = ABBR_ALIASES[sportKey][key] || key
  return TEAM_COLORS[sportKey][resolved] || FALLBACK
}

// ── Contrast-checked chip style ─────────────────────────────────────────────
// Required treatment app-wide: background = primary, text = secondary — that's
// what makes two clubs with similar primaries (e.g. NBA HOU/CHI/TOR, all
// #CE1141-ish reds) still read as distinct rather than both defaulting to the
// same safe ink. But several secondary/primary pairs don't clear a readable
// contrast ratio at chip sizes (e.g. NBA CHI: #CE1141 primary vs #000000
// secondary is fine, but several dark-on-dark pairs aren't) — so this checks
// WCAG contrast at render time and falls back to the precomputed `ink` rather
// than hand-maintaining an exceptions list.

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const num = parseInt(full, 16)
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}

function relativeLuminance({ r, g, b }) {
  const [R, G, B] = [r, g, b].map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

function contrastRatio(hexA, hexB) {
  const lA = relativeLuminance(hexToRgb(hexA))
  const lB = relativeLuminance(hexToRgb(hexB))
  const [lighter, darker] = lA > lB ? [lA, lB] : [lB, lA]
  return (lighter + 0.05) / (darker + 0.05)
}

// Returns { background, color } — background is always the club's primary;
// color is the club's secondary if secondary-on-primary clears 4.5:1,
// otherwise the precomputed `ink`.
export function teamChipStyle(abbr, sport = 'nba') {
  const c = teamColor(abbr, sport)
  const secondaryOk = contrastRatio(c.secondary, c.primary) >= 4.5
  return { background: c.primary, color: secondaryOk ? c.secondary : c.ink }
}
